import type { BalanceData, MilestoneView } from "@highmarch/domain";
import type { MilestoneClaim } from "@highmarch/app";
import { btnHtml, fmtCompact } from "../kit";
import { icon } from "../iconsKit";
import { checkHtml, wellHtml } from "../components";
import { forgeChipsHtml, gemGlyph, marksGlyph, paidLine, rewardArt, rewardWords, trophyChipHtml } from "../hub/eventRewards";
import { paidContents, rewardChest } from "../chestReveal";
import { econCue } from "../hub/purchaseFx";
import { toast } from "../toast";
import { refusalWords } from "../hub/refusalWords";
import { infinityGlyph, ironStarGlyph } from "../modeGlyphs";
import { CHEST_LABEL } from "./forgeWords";
import { bindRewardFocus, dotHtml, rewardFocusAttr } from "../hub/evKit";
import "../styles/evTracks.css";

/**
 * The free routes' milestone tracks (gear v2, docs/GEAR_V2.md §4): the Endless weekly milestones (on the Endless
 * sheet) and the Iron star totals (on the Iron sheet), and both on the Free rewards sheet. One ladder for both: a rail
 * filled to the player's progress, a node per milestone (its wave or star count), the reward's art and words, and the
 * row's state — CLAIM (gold) once reached, a tick once claimed, "n to go" before. Markup + the claim's reveal; the
 * rules (what is reached, what a claim pays) are the app's Modes.
 */

export type TrackKind = "endless" | "iron";

/** What a row says under its reward: claimed, ready, how far to go (and, for Endless, which week). */
export function milestoneState(m: MilestoneView, kind: TrackKind, thisWeek: number): { readonly cls: string; readonly note: string } {
  const unit = kind === "endless" ? "wave" : "Iron star";
  const last = kind === "endless" && m.week !== thisWeek ? " · last week" : "";
  if (m.claimed) return { cls: "claimed", note: `Claimed${last}` };
  if (m.reached) return { cls: "ready", note: kind === "endless" ? `Reached wave ${m.milestone.count}${last}` : `${m.milestone.count} Iron stars reached` };
  const left = m.milestone.count - m.have;
  return { cls: "todo", note: `${left} more ${unit}${left === 1 ? "" : "s"} · ${m.have}/${m.milestone.count}` };
}

/** The rail's fill (0…1): how far the player is between the first and the last node. */
export function railFill(rows: readonly MilestoneView[]): number {
  if (rows.length === 0) return 0;
  const reached = rows.filter((r) => r.reached).length;
  if (reached === rows.length) return 1;
  const prev = reached === 0 ? 0 : rows[reached - 1].milestone.count, next = rows[reached].milestone.count;
  const part = next > prev ? Math.max(0, Math.min(1, (rows[reached].have - prev) / (next - prev))) : 0;
  return Math.min(1, (reached - 1 + part + (reached === 0 ? 1 : 0)) / Math.max(1, rows.length - 1));
}

const ART_PX = 30;

/** The ladder's markup. Rows carry `data-w` (week) and `data-i` (index) for the claim. */
export function milestoneTrackHtml(rows: readonly MilestoneView[], kind: TrackKind, thisWeek: number): string {
  if (rows.length === 0) return "";
  const glyph = kind === "endless" ? infinityGlyph(14) : ironStarGlyph(14);
  const items = rows.map((m) => {
    const st = milestoneState(m, kind, thisWeek);
    const r = m.milestone.reward;
    const well = wellHtml({ art: rewardArt(r, ART_PX), rarity: r.chest, size: "sm", dim: !m.reached });
    const chips = `${r.chest !== null ? `<span class="rw">${CHEST_LABEL[r.chest]} chest</span>` : ""}${r.gems > 0 ? `<span class="rw">${gemGlyph(14)}${r.gems}</span>` : ""}${r.marks > 0 ? `<span class="rw">${marksGlyph(14)}${fmtCompact(r.marks)}</span>` : ""}${forgeChipsHtml(r, 14)}${(r.trophies ?? 0) > 0 ? trophyChipHtml(r.trophies ?? 0, 14) : ""}`;
    const act = m.claimed ? checkHtml("done")
      : m.reached ? btnHtml("CLAIM", "gold", { className: "disp mtclaim", data: `data-w="${m.week}" data-i="${m.index}"` })
      : `<span class="lock">${icon("lock", 14)}</span>`;
    return `<div class="mtrow ${st.cls}" data-w="${m.week}" data-i="${m.index}" aria-label="${m.milestone.count} ${kind === "endless" ? "waves" : "Iron stars"}: ${rewardWords(r)}. ${st.note}">
      <span class="node disp">${glyph}<b>${m.milestone.count}</b></span><button type="button" class="evtw mtw" ${rewardFocusAttr(r)}>${well}</button>
      <span class="txt"><span class="chips">${chips}</span><small>${st.note}</small></span><span class="act">${act}</span>${st.cls === "ready" ? dotHtml(1, true) : ""}</div>`;
  }).join("");
  return `<div class="mtrack ${kind}" style="--fill:${railFill(rows).toFixed(3)}"><i class="rail"><i class="on"></i></i>${items}</div>`;
}

/**
 * Wires the CLAIM buttons of every track under `host`: `claim(week, index)` pays it (Modes.claim*); a chest opens in a
 * reveal over `layerHost`, anything else is a toast. `after` redraws the sheet.
 */
export function bindMilestoneClaims(host: ParentNode, ctx: { readonly balance: BalanceData; readonly layerHost: HTMLElement; readonly toastHost: HTMLElement },
  claim: (kind: TrackKind, week: number, index: number) => MilestoneClaim | string, after: () => void): void {
  bindRewardFocus(host, ctx.toastHost, ctx.balance, 1);
  for (const b of Array.from(host.querySelectorAll<HTMLButtonElement>(".mtclaim"))) {
    b.onclick = (e) => {
      e.stopPropagation();
      const track = b.closest<HTMLElement>(".mtrack");
      const kind: TrackKind = track?.classList.contains("iron") ? "iron" : "endless";
      const c = claim(kind, Number(b.dataset.w), Number(b.dataset.i));
      if (typeof c === "string") { toast(ctx.toastHost, refusalWords(c)); after(); return; }
      econCue("event");
      showClaim(ctx, c, after);
    };
  }
}

/** What a claim paid: a chest reveal (with the essence and sigils on its loot card) or a toast. */
export function showClaim(ctx: { readonly balance: BalanceData; readonly layerHost: HTMLElement; readonly toastHost: HTMLElement }, c: MilestoneClaim, after: () => void): void {
  const line = paidLine(c.paid, c.gear);
  if (c.paid.chest) {
    rewardChest(ctx.layerHost, ctx.balance, c.paid.chest.rarity, paidContents(c.paid, c.gear), line, after);   // M30 #10
    return;
  }
  toast(ctx.toastHost, line, 2400);
  after();
}
